import get from 'lodash/get';
import reduce from 'lodash/reduce';
import uuidv4 from 'uuid/v4';
import omit from 'lodash/omit';

import { VARIANTS } from '@jobcase/react_pattern_library/components/Toaster/Toaster';

import config from '../../../config';
import fetch from '../../../utils/fetch';
import logger from '../../../utils/logger';

import {
  SET_IS_POLICY_ACCEPTED,
  SET_IS_NAV_COLLAPSED,
} from './actionTypes';
import { setNotification } from '../notification/actions';

const CLOUDFLARE_HEADERS = [
  'cf-connecting-ip',
  'cf-bot-score',
  'cf-verified-bot',
  'cf-ipcountry',
  'cf-ipregion',
  'cf-ipcity',
  'cf-ippostalcode',
  'cf-asn',
  'cf-colo',
];

const GET_TESTING_KEYS = 'application/GET_TESTING_KEYS';

export const getAgreementAcceptStatus = () => async (dispatch) => {
  try {
    const response = await fetch(`${config.apiUrl}/v1/legal-agreement/status`);
    if (response) {
      dispatch({ type: SET_IS_POLICY_ACCEPTED, payload: response.data.has_accepted_agreement });
    }
  } catch (error) {
    throw error;
  }
};

export const updateAgreementAcceptStatus = accept => async (dispatch) => {
  try {
    const response = await fetch(`${config.apiUrl}/v1/legal-agreement/accept`, {
      method: 'POST',
      data: {
        has_accepted_agreement: accept,
      },
    });
    if (response) {
      dispatch(setNotification('Thank you for accepting Jobcase Services Agreement.', VARIANTS.SUCCESS));
      dispatch({ type: SET_IS_POLICY_ACCEPTED, payload: response.data.has_accepted_agreement });
    }
  } catch (err) {
    // Do Nothing
  } finally {
    dispatch(setNotification('Thank you for accepting Jobcase Services Agreement.', VARIANTS.SUCCESS));
    dispatch({ type: SET_IS_POLICY_ACCEPTED, payload: true });
  }
};

export const setIsNavCollapsed = isCollapsed => dispatch => dispatch({ type: SET_IS_NAV_COLLAPSED, payload: isCollapsed });

const SET_ARRIVAL_LOCK = 'application/SET_ARRIVAL_LOCK';

const setArrivalLock = req => async (dispatch, getState) => {
  const state = getState();
  const arrivalLock = get(state, 'application.arrivalLock');
  const newArrivalLock = uuidv4();

  // Log exception if we create arrival lock for same session more than once.
  // Each session should receive only one arrival lock so all calls use same arrival.
  if (arrivalLock) {
    logger.logWarning('Generated arrival lock more than once', {
      arrivalLock,
      newArrivalLock,
      query: req.query,
      requestUrl: req.url,
    });
  }

  await dispatch({ type: SET_ARRIVAL_LOCK, payload: arrivalLock || newArrivalLock });
};

const SET_BROWSER_REFERER = 'application/SET_BROWSER_REFERER';

const setBrowserReferer = req => async (dispatch, getState) => {
  const state = getState();
  const browserReferer = get(state, 'application.browserReferer');
  const newBrowserReferer = get(req, 'headers.referer');
  // Log exception if we create a browser referer for the same session more than once.
  // Each session should use the same browser referer
  if (browserReferer) {
    logger.logWarning('Generated browser referer more than once', {
      browserReferer,
      newBrowserReferer,
      query: req.query,
      requestUrl: req.url,
    });
  }

  dispatch({ type: SET_BROWSER_REFERER, payload: browserReferer || newBrowserReferer });
};

const SET_CLOUDFLARE_HEADERS = 'application/SET_CLOUDFLARE_HEADERS';

const setCloudflareHeaders = req => async (dispatch, getState) => {
  const state = getState();

  const newRequestHeaders = reduce(CLOUDFLARE_HEADERS, (result, value) => {
    const currentHeaderValue = get(state, `application.cloudflareHeaders.${value}`);
    const newHeaderValue = get(req, `headers.${value}`);

    return { ...result, [value]: currentHeaderValue || newHeaderValue };
  }, {});

  dispatch({ type: SET_CLOUDFLARE_HEADERS, payload: newRequestHeaders });
};

const SET_SHOW_SSO_MODAL = 'application/SET_SHOW_SSO_MODAL';

const setShowSSOModal = options => (dispatch) => {
  const showModal = get(options, 'showModal');
  const ssoProps = omit(options, ['showModal', 'onLoginSuccess', 'onClickDoThisLater']);
  const onLoginSuccess = get(options, 'onLoginSuccess');
  const onClickDoThisLater = get(options, 'onClickDoThisLater');
  dispatch({ type: SET_SHOW_SSO_MODAL, payload: { showModal, props: ssoProps }, refs: { onLoginSuccess, onClickDoThisLater } });
};

const HIDE_SSO_MODAL = 'application/HIDE_SSO_MODAL';

const hideSSOModal = () => (dispatch) => {
  dispatch({ type: HIDE_SSO_MODAL });
};

const subscribeToWebPush = subscription => async () => {
  const pushSubscription = JSON.parse(JSON.stringify(subscription));
  delete pushSubscription.expirationTime;
  const data = {
    ARRIVAL_WEB_PUSH_SUBSCRIPTION: {
      applicationServerKey: config.vapidKey,
      pushSubscription,
    },
  };

  try {
    await fetch(`${config.apiUrl}/v1/arrivals`, {
      method: 'PATCH',
      body: data,
      fullResponse: true,
    });
  } catch (error) {
    logger.logError(`Error subscribing to webPush via Odin: ${error.message}`);
  }
};

export {
  GET_TESTING_KEYS,
  SET_ARRIVAL_LOCK,
  setArrivalLock,
  SET_BROWSER_REFERER,
  setBrowserReferer,
  SET_CLOUDFLARE_HEADERS,
  setCloudflareHeaders,
  SET_SHOW_SSO_MODAL,
  setShowSSOModal,
  HIDE_SSO_MODAL,
  hideSSOModal,
  subscribeToWebPush,
};

export default {
  GET_TESTING_KEYS,
  setIsNavCollapsed,
  SET_IS_NAV_COLLAPSED,
  SET_ARRIVAL_LOCK,
  setArrivalLock,
  SET_BROWSER_REFERER,
  setBrowserReferer,
  SET_CLOUDFLARE_HEADERS,
  setCloudflareHeaders,
  SET_SHOW_SSO_MODAL,
  setShowSSOModal,
  HIDE_SSO_MODAL,
  hideSSOModal,
  subscribeToWebPush,
  SET_IS_POLICY_ACCEPTED,
};
